import { fadedBlack30, useTheme } from '@backoffice-frontend/patterns'
import { HomeIcon } from '@moia-dev/pace-icons'

export const PureHubMarker = ({
  hubName,
  isClickable = false,
  isFaded = false,
  isSelected = false,
  returningVehiclesCount,
  showReturningVehiclesLabel = false,
  vehicleCount,
}: {
  hubName: string
  isClickable?: boolean
  isFaded?: boolean
  isSelected?: boolean
  returningVehiclesCount?: number
  showReturningVehiclesLabel?: boolean
  vehicleCount?: number
}) => {
  const theme = useTheme()

  const hasVehicleCount = !!vehicleCount && vehicleCount > 0
  const hasReturningVehicleCount =
    !!returningVehiclesCount && returningVehiclesCount > 0

  const styles = {
    root: {
      alignItems: 'center',
      backgroundColor: theme.semantic.ColorSurfaceDefault,
      border: `1px solid ${theme.semantic.ColorBackgroundDimmedReduced}`,
      borderRadius: 4,
      cursor: 'default',
      display: 'flex',
      flexWrap: 'wrap',
      height: 32,
      justifyContent: 'center',
      position: 'relative',
      width: 32,
      zIndex: 3,

      '&:hover': {
        opacity: 1,

        '& .tooltip': {
          opacity: 1,
          transform: 'translateY(0)',
        },

        ...(!isSelected && {
          backgroundColor: theme.semantic.ColorAccessoryReduced,

          '& .vehicleCountLabel': {
            background: theme.semantic.ColorActionSecondaryHovered,
          },
        }),
      },

      ...(!isSelected && {
        '&:active': {
          backgroundColor: theme.semantic.ColorAccessoryDefault,

          '& .vehicleCountLabel': {
            background: theme.semantic.ColorActionSecondaryPressed,
          },
        },
      }),
    } as const,

    selected: {
      '& .vehicleCountLabel': {
        background: theme.semantic.ColorAccentDefault,
      },

      '& svg': {
        color: theme.semantic.ColorAccentDefault,
      },
    },

    withCount: {
      flexFlow: 'row',
      width: 'auto',
    },

    iconWithCount: {
      marginLeft: 4,
      marginRight: 4,
    },

    tooltip: {
      backgroundColor: theme.semantic.ColorBackgroundDefault,
      borderRadius: 2,
      bottom: isSelected ? 36 : 38,
      boxShadow: `${fadedBlack30} 0px 1px 4px -1px`,
      color: theme.semantic.ColorContentDefault,
      opacity: 0,
      padding: theme.spacing(0.5, 1),
      pointerEvents: 'none',
      position: 'absolute',
      textAlign: 'center',
      transform: 'translateY(2px)',
      transition: 'opacity 0.3s ease-in-out, transform 0.3s ease-in-out',
      userSelect: 'none',
      whiteSpace: 'nowrap',
      zIndex: 3,
    } as const,

    markerContent: {
      alignItems: 'center',
      display: 'flex',
      height: '100%',
      color: theme.semantic.ColorActionSecondaryDefault,
    },

    commonLabelStyles: {
      alignItems: 'center',
      display: 'flex',
      fontWeight: 'bold',
      fontSize: 16,
      height: '100%',
      padding: theme.spacing(0, 1),
      transition: 'background 0.3s ease-in-out',
    },

    vehicleCountLabel: {
      background: isSelected
        ? theme.semantic.ColorAccentDefault
        : theme.semantic.ColorBackgroundDimmedDefault,
      borderBottomRightRadius:
        showReturningVehiclesLabel && hasReturningVehicleCount ? 0 : 4,
      borderTopRightRadius:
        showReturningVehiclesLabel && hasReturningVehicleCount ? 0 : 4,
      color: theme.semantic.ColorContentOnAccentDefault,
    },

    returningVehiclesCountLabel: {
      background: theme.semantic.ColorBackgroundAlternative,
      color: theme.semantic.ColorAccentDefault,
      borderBottomRightRadius: 4,
      borderTopRightRadius: 4,
    },

    clickable: {
      cursor: 'pointer',
    },

    faded: {
      opacity: 0.5,
      transition: 'opacity 0.3s ease-in-out',
    },
  }

  return (
    <div
      css={{
        ...styles.root,
        ...(hasVehicleCount && styles.withCount),
        ...(isFaded && styles.faded),
        ...(isClickable && styles.clickable),
        ...(isSelected && styles.selected),
      }}
    >
      <span className="tooltip" css={styles.tooltip}>
        {hubName}
      </span>
      <div className="markerContent" css={styles.markerContent}>
        <HomeIcon
          css={{
            height: '20px',
            width: '20px',
            ...(hasVehicleCount && styles.iconWithCount),
          }}
        />

        {hasVehicleCount && (
          <span
            className="vehicleCountLabel"
            css={[styles.commonLabelStyles, styles.vehicleCountLabel]}
          >
            {vehicleCount}
          </span>
        )}

        {showReturningVehiclesLabel && hasReturningVehicleCount && (
          <span
            className="returningVehiclesCountLabel"
            css={[styles.commonLabelStyles, styles.returningVehiclesCountLabel]}
          >
            +{returningVehiclesCount}
          </span>
        )}
      </div>
    </div>
  )
}
